<template>
  <v-card style="border-left: 8px solid #004d99">
    <v-card-title>
      <span class="units-table-title">Resultados por produto (gatilho)</span>
      <v-spacer />
      <export-data :data-fetcher="getDataToExport" filename="resultados_produto_gatilho" ghost />
      <section class="d-flex align-center justify-end">
        <headers-data-table-settings
          :key="headersSettingsName"
          :headers.sync="headers"
          :default-headers="getDefaultHeadersOptions()"
          :headers-settings-name="headersSettingsName"
        />
      </section>
      <div class="min-width-345">
        <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Buscar por produto" />
      </div>
    </v-card-title>

    <v-data-table :headers="computedHeaders" :items="items" :search="search" :footer-props="footerProps" sort-desc>
      <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
        <mf-tooltip v-bind:key="h.text" top max-width="192px" :disabled="!h.tooltip">
          <template v-slot:activator="{ on }">
            {{ h.text }}
            <mf-icon v-show="h.tooltip" small color="grey" v-on="on">info</mf-icon>
          </template>
          <span>{{ h.tooltip }}</span>
        </mf-tooltip>
      </template>

      <template v-slot:item.revenue="{ item }">
        <span>{{ localeString(item.revenue, 2) | currency }}</span>
      </template>

      <template v-if="items.length > 0" v-slot:body.append="{ headers, items }">
        <tr style="font-weight: bold">
          <td v-for="(header, i) in headers" :key="i">
            <div v-if="header.value === 'sku'">
              <mf-tooltip top max-width="192px">
                <template v-slot:activator="{ on }">
                  <div class="d-flex align-center">
                    <span>Total:</span>
                    <mf-icon class="ml-1" small color="cinza-0" v-on="on"> info </mf-icon>
                  </div>
                </template>
                <span>
                  O total é calculado considerando clientes e cupons únicos. Um mesmo cliente pode comprar diferentes produtos, e um produto pode aparecer em
                  diversos cupons. Por isso a soma dos valores da tabela pode não ser igual ao total.
                </span>
              </mf-tooltip>
            </div>

            <div v-if="header.value === 'name'"></div>

            <div v-if="header.value === 'sold_units'" class="text-center">
              {{ itemsTotal.sold_units }}
            </div>

            <div v-if="header.value === 'eligible_products'" class="text-center">
              {{ itemsTotal.eligible_products }}
            </div>

            <div v-if="header.value === 'clients'" class="text-center">
              {{ itemsTotal.clients }}
            </div>

            <div v-if="header.value === 'coupons'" class="text-center">
              {{ itemsTotal.coupons }}
            </div>

            <div v-if="header.value === 'revenue'" class="text-center">
              {{ localeString(itemsTotal.revenue, 2) | currency }}
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { localeString } from '@/config/helpers.js'
import cloneDeep from 'lodash/cloneDeep'
import ExportData from '@/components/export-data/ExportData.vue'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'

export default {
  components: {
    ExportData,
    HeadersDataTableSettings,
  },
  props: {
    metrics: {
      type: Object,
      default(_) {
        return {}
      },
    },
    triggerProducts: {
      type: Array,
      default(_) {
        return []
      },
    },
  },
  data: () => ({
    search: '',
    headers: [],
  }),
  computed: {
    defaultHeaders() {
      return [
        {
          text: 'Codigo',
          align: 'left',
          value: 'sku',
          width: '10%',
          sortable: true,
          filterable: true,
          isActive: true,
          isRequired: true,
          isDefault: true,
        },
        {
          text: 'Produto',
          align: 'left',
          value: 'name',
          width: '20%',
          sortable: true,
          filterable: true,
          isActive: true,
          isRequired: true,
          isDefault: true,
        },
        {
          text: 'Unidades vendidas',
          align: 'center',
          value: 'sold_units',
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          tooltip: 'Total de vendas dos SKU dentro da oferta no período',
        },
        {
          text: 'Unidades elegíveis',
          align: 'center',
          value: 'eligible_products',
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          tooltip: 'Produtos gatilho que cumpriram os requisitos para aplicação do desconto',
        },
        {
          text: 'Clientes',
          align: 'center',
          value: 'clients',
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          tooltip: 'CPF únicos que compraram os produtos',
        },
        {
          text: 'Cupons',
          align: 'center',
          value: 'coupons',
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          tooltip: 'Cupons da oferta que possuem este produto',
        },
        {
          text: 'Faturamento do produto',
          align: 'center',
          value: 'revenue',
          sortable: true,
          isActive: true,
          isRequired: false,
          isDefault: true,
          tooltip: 'Faturamento gerado nesta oferta',
        },
      ]
    },

    items() {
      const items = []
      this.metrics?.products_metrics?.table?.forEach((pm) => {
        items.push({
          sku: pm?.product?.sku,
          name: pm?.product?.name,
          sold_units: pm?.total?.quantity,
          eligible_products: pm?.eligible?.quantity,
          clients: pm?.sales?.clients,
          coupons: pm?.sales?.tickets,
          revenue: pm?.sales?.revenue,
        })
      })

      if (this.triggerProducts.length > 0) {
        const itemsSku = items.map((item) => item.sku)
        const missingProducts = this.triggerProducts.filter((triggerProduct) => !itemsSku.includes(triggerProduct.sku))
        missingProducts.forEach((tp) => {
          items.push({
            sku: tp.sku,
            name: tp.name,
            sold_units: 0,
            eligible_products: 0,
            clients: 0,
            coupons: 0,
            revenue: 0,
          })
        })
      }

      return items
    },

    itemsTotal() {
      const totals = this.metrics?.products_metrics?.totals
      const eligibleMetrics = this.metrics?.eligible_metrics
      return {
        sold_units: totals?.total.quantity,
        eligible_products: eligibleMetrics?.quantity,
        clients: totals?.sales?.clients,
        coupons: totals?.sales?.tickets,
        revenue: totals?.sales?.revenue,
      }
    },

    computedHeaders() {
      return this.headers.filter((header) => header.isActive)
    },

    footerProps() {
      return { itemsPerPageOptions: [10, 15, 20] }
    },

    headersSettingsName() {
      return 'trigger-products-th'
    },
  },
  methods: {
    localeString,

    getDataToExport() {
      const headers = this.computedHeaders.map((header) => header.text)
      const rows = this.items.map((item) => this.computedHeaders.map((header) => item[header.value]))
      return { headers, rows }
    },

    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },
  },
}
</script>

<style lang="scss" scoped>
.units-table-title {
  color: $cinza-d10;
  font-weight: bold;
  font-size: 18px;
}

.min-width-345 {
  min-width: 345px;
  background-color: white;
}
</style>
